export interface RuntimeConfig {
  apiUrl: string;
  chatApiUrl?: string;
  authenticationMethod: string;
  auth0Domain: string;
  auth0ClientId: string;
  auth0Audience: string;
  paymentMethod: string;
  theme: string;
  enableDevFeatures: string;
  enableEmailAdmin: string;
  maintenanceMode: string;
}

declare global {
  interface Window {
    __METALMIND_RUNTIME_CONFIG__?: Partial<RuntimeConfig>;
  }
}

const runtimeConfigKeys: (keyof RuntimeConfig)[] = [
  "apiUrl",
  "authenticationMethod",
  "auth0Domain",
  "auth0ClientId",
  "auth0Audience",
  "paymentMethod",
  "theme",
  "enableDevFeatures",
  "enableEmailAdmin",
  "maintenanceMode",
];

const nonEmptyRuntimeConfigKeys: (keyof RuntimeConfig)[] = [
  "apiUrl",
  "authenticationMethod",
  "paymentMethod",
  "theme",
  "enableDevFeatures",
  "enableEmailAdmin",
  "maintenanceMode",
];

export function getRuntimeConfig(): RuntimeConfig {
  const config = window.__METALMIND_RUNTIME_CONFIG__;
  if (!config) {
    throw new Error(
      "Runtime config missing. Ensure /runtime-config.js loads before the app bundle.",
    );
  }

  const missingKeys = runtimeConfigKeys.filter(
    (key) => !Object.prototype.hasOwnProperty.call(config, key),
  );
  const invalidKeys = runtimeConfigKeys.filter(
    (key) =>
      Object.prototype.hasOwnProperty.call(config, key) &&
      typeof config[key] !== "string",
  );
  if (
    Object.prototype.hasOwnProperty.call(config, "chatApiUrl") &&
    typeof config.chatApiUrl !== "string"
  ) {
    invalidKeys.push("chatApiUrl");
  }
  const emptyKeys = nonEmptyRuntimeConfigKeys.filter((key) => {
    const value = config[key];
    return (
      Object.prototype.hasOwnProperty.call(config, key) &&
      typeof value === "string" &&
      value.trim() === ""
    );
  });

  if (
    missingKeys.length > 0 ||
    invalidKeys.length > 0 ||
    emptyKeys.length > 0
  ) {
    throw new Error(
      [
        missingKeys.length > 0 ? `missing keys: ${missingKeys.join(", ")}` : "",
        invalidKeys.length > 0 ? `invalid keys: ${invalidKeys.join(", ")}` : "",
        emptyKeys.length > 0 ? `empty keys: ${emptyKeys.join(", ")}` : "",
      ]
        .filter(Boolean)
        .join("; "),
    );
  }

  return config as RuntimeConfig;
}

export function getApiBaseUrl(): string {
  return getRuntimeConfig().apiUrl.replace(/\/$/, "");
}

export function getIdaChatApiBaseUrl(): string {
  const config = getRuntimeConfig();
  return (config.chatApiUrl?.trim() || config.apiUrl).replace(/\/$/, "");
}

export function getAuthenticationMethod(): string {
  return getRuntimeConfig().authenticationMethod.toUpperCase();
}

export function getThemeName(): string {
  return getRuntimeConfig().theme;
}

export function isMockApiRuntime(): boolean {
  return getApiBaseUrl() === "http://localhost:3001";
}

// Defense-in-depth for AUTHENTICATION_METHOD=UNSAFE. The bypass auth path
// (UnsafeAuthProvider + ?user_name= query) MUST never activate on a deployed
// host -- env-var discipline is not sufficient on its own. Used by
// AuthProvider to refuse UNSAFE selection unless the FE is actually rendering
// on a loopback address.
export function isLocalhostHostname(): boolean {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "0.0.0.0" ||
    host === "::1"
  );
}

export function isRuntimeFlagEnabled(value: string): boolean {
  return value.toLowerCase() === "true";
}
